import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ASSETS } from "@/lib/data";

// Synthesized gong hit via Web Audio API — no external audio file needed.
// Inharmonic partials with fast attack + long exponential decay, plus a short noise strike.
const playGong = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();

    const strike = () => {
      if (ctx.state !== "running") return;
      const t0 = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.55, t0);
      master.connect(ctx.destination);

      // Gong partials: [frequency ratio, gain, decay seconds]
      const f0 = 98;
      const partials = [
        [1.0, 1.0, 3.4],
        [1.19, 0.6, 2.8],
        [1.48, 0.5, 2.4],
        [1.98, 0.34, 1.9],
        [2.42, 0.22, 1.5],
        [2.94, 0.16, 1.2],
        [3.5, 0.1, 0.95],
        [4.1, 0.07, 0.75],
      ];
      partials.forEach(([ratio, gain, decay]) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f0 * ratio, t0);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(gain * 0.28, t0 + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
        osc.connect(g).connect(master);
        osc.start(t0);
        osc.stop(t0 + decay + 0.1);
      });

      // Strike noise burst (the mallet hit)
      const noiseLen = 0.09;
      const buffer = ctx.createBuffer(1, ctx.sampleRate * noiseLen, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const band = ctx.createBiquadFilter();
      band.type = "bandpass";
      band.frequency.setValueAtTime(1400, t0);
      band.Q.setValueAtTime(0.8, t0);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.5, t0);
      ng.gain.exponentialRampToValueAtTime(0.0001, t0 + noiseLen);
      noise.connect(band).connect(ng).connect(master);
      noise.start(t0);
    };

    if (ctx.state === "running") strike();
    else
      ctx
        .resume()
        .then(strike)
        .catch(() => {});

    setTimeout(() => ctx.close().catch(() => {}), 5000);
  } catch {
    /* audio unsupported — popup still works silently */
  }
};

export const EventPopup = () => {
  const [open, setOpen] = useState(false);
  const gongPlayed = useRef(false);
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);

  const boom = useCallback(() => {
    if (gongPlayed.current) return;
    gongPlayed.current = true;
    playGong();
  }, []);

  // Show on every visit, shortly after the page loads
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);

  // Sound + scroll freeze + Escape while open.
  // Browsers block audio before the first user gesture — if blocked, retry once on the next tap.
  useEffect(() => {
    if (!open) return;
    boom();
    const retry = () => boom();
    window.addEventListener("pointerdown", retry, { once: true });
    const lenis = window.__lenis;
    lenis?.stop();
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      lenis?.start();
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", retry);
    };
  }, [open, close, boom]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-void/90 p-4 backdrop-blur-md"
          onClick={close}
          data-testid="event-popup"
        >
          <motion.div
            initial={{ scale: 0.75, y: 40, opacity: 0, filter: "brightness(2.2) blur(8px)" }}
            animate={{ scale: 1, y: 0, opacity: 1, filter: "brightness(1) blur(0px)" }}
            exit={{ scale: 0.94, y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 160, damping: 20 }}
            className="relative w-full max-w-sm cursor-pointer sm:max-w-md lg:max-w-xl"
          >
            <div className="mb-3 flex items-center justify-center gap-2" data-testid="event-popup-badge">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon-pink opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-neon-pink" />
              </span>
              <span className="whitespace-nowrap font-mono-cap text-[9px] text-neon-pink sm:text-[11px]">Happening Now · 26–28 November 2026</span>
            </div>
            <img
              src={ASSETS.popupPoster}
              alt="WPC 3.0 — The Rise of Pahlawan, 26–28 November 2026, Stadium Titiwangsa, Kuala Lumpur"
              className="mx-auto max-h-[58vh] w-auto max-w-full rounded-2xl border border-white/15 object-contain glow-ring lg:max-h-[72vh]"
              data-testid="event-popup-poster"
            />
            <button
              onClick={() => {
                close();
                navigate("/tickets");
              }}
              className="group relative mt-4 w-full overflow-hidden rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-8 py-3.5 font-mono-cap text-[12px] text-white transition-transform duration-300 hover:scale-[1.02]"
              data-testid="event-popup-cta"
            >
              <span className="relative z-10">Get Tickets</span>
            </button>
            <button
              onClick={close}
              className="mt-2 w-full py-3 text-center font-mono-cap text-[11px] tracking-widest text-slate-400 transition-colors hover:text-white"
              data-testid="event-popup-skip"
            >
              Skip
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
